import { useEffect, useState } from "react";
import { Loader2, Plus, ShieldCheck, ShieldX, Trash2, Unlock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

const err = (e, fallback) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);

const hoursLeft = (iso) => {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "wygasa";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
};

export function SecurityAdmin() {
  const [data, setData] = useState(null);
  const [myIp, setMyIp] = useState("");
  const [form, setForm] = useState({ ip: "", label: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/security/blocks").then((r) => setData(r.data)).catch(() => setData({ blocks: [], whitelist: [] }));
  useEffect(() => {
    load();
    api.get("/admin/security/whoami").then((r) => setMyIp(r.data.ip)).catch(() => {});
  }, []);

  const unblock = async (ip) => {
    if (!window.confirm(`Odblokować adres ${ip} teraz?`)) return;
    try { await api.post("/admin/security/unblock", { ip }); toast.success(`Odblokowano ${ip}.`); load(); } catch (e) { err(e, "Nie udało się odblokować."); }
  };
  const addWhitelist = async (e, ip) => {
    if (e) e.preventDefault();
    const payload = ip ? { ip, label: "" } : form;
    if (!payload.ip.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/security/whitelist", payload);
      toast.success(`Dodano ${payload.ip} do zaufanych adresów.`);
      setForm({ ip: "", label: "" });
      load();
    } catch (er) { err(er, "Nie udało się dodać adresu."); } finally { setBusy(false); }
  };
  const removeWhitelist = async (ip) => {
    if (!window.confirm(`Usunąć ${ip} z listy zaufanych adresów?`)) return;
    try { await api.delete(`/admin/security/whitelist/${encodeURIComponent(ip)}`); toast.success(`Usunięto ${ip}.`); load(); } catch (e) { err(e, "Błąd usuwania."); }
  };

  const blocks = data?.blocks || [];
  const whitelist = data?.whitelist || [];
  const myIpTrusted = whitelist.some((w) => w.ip === myIp);

  return (
    <div data-testid="security-admin" className="space-y-6">
      {/* Twój adres IP */}
      <div className="bg-card border border-border rounded-2xl p-6">
        <h3 className="font-display font-bold text-lg">Twój obecny adres IP</h3>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span data-testid="security-my-ip" className="font-mono text-sm px-3 py-1.5 rounded-full bg-muted">{myIp || "—"}</span>
          {myIp && !myIpTrusted && (
            <button data-testid="security-trust-my-ip" onClick={() => addWhitelist(null, myIp)} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-brand-teal hover:bg-brand-tealDark text-white rounded-full px-3 py-1.5 transition-colors">
              <ShieldCheck size={13} /> Dodaj mój adres do zaufanych
            </button>
          )}
          {myIp && myIpTrusted && <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-teal"><ShieldCheck size={13} /> Ten adres jest już zaufany</span>}
        </div>
      </div>

      {/* Aktywne blokady */}
      <div className="bg-card border border-border rounded-2xl">
        <div className="p-6 pb-3">
          <h3 className="font-display font-bold text-lg">Aktywne blokady IP</h3>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">Adresy zablokowane po nieudanych logowaniach lub z linku „To nie ja” w mailu. Blokada znika automatycznie po 24 h — tu możesz zdjąć ją wcześniej.</p>
        </div>
        <div className="divide-y divide-border border-t border-border">
          {!data && <div className="p-6 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>}
          {data && !blocks.length && <p data-testid="security-blocks-empty" className="p-6 text-sm text-muted-foreground">Brak aktywnych blokad. 👌</p>}
          {blocks.map((b) => (
            <div key={b.ip} data-testid={`security-block-${b.ip}`} className="p-4 sm:px-6 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px]">
                <p className="font-mono font-semibold text-sm">{b.ip}</p>
                <p className="text-xs text-muted-foreground">
                  Zablokowano {new Date(b.created_at).toLocaleString("pl-PL")} · wygasa {new Date(b.expires_at).toLocaleString("pl-PL")} · pozostało {hoursLeft(b.expires_at)}
                </p>
              </div>
              <button data-testid={`security-unblock-${b.ip}`} onClick={() => unblock(b.ip)} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-3 py-1.5 hover:bg-muted"><Unlock size={13} /> Odblokuj teraz</button>
              <button data-testid={`security-trust-${b.ip}`} onClick={() => addWhitelist(null, b.ip)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-teal border border-brand-teal/30 rounded-full px-3 py-1.5 hover:bg-brand-teal/10"><ShieldCheck size={13} /> Zaufaj</button>
            </div>
          ))}
        </div>
      </div>

      {/* Whitelist */}
      <div className="bg-card border border-border rounded-2xl">
        <div className="p-6 pb-3">
          <h3 className="font-display font-bold text-lg">Zaufane adresy IP biura (whitelista)</h3>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">Adresy z tej listy <strong>nigdy</strong> nie są blokowane (ani automatycznie, ani z linku w mailu) i <strong>nie generują</strong> alertów o nieudanym logowaniu. Dodaj tu stały adres IP swojego biura.</p>
          <form onSubmit={addWhitelist} className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <div><label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Adres IP</label><Input data-testid="security-whitelist-ip" value={form.ip} onChange={(e) => setForm({ ...form, ip: e.target.value })} placeholder="np. 85.128.10.20" required /></div>
            <div><label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Etykieta (opcjonalnie)</label><Input data-testid="security-whitelist-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="np. Biuro Domaniewska" /></div>
            <button type="submit" data-testid="security-whitelist-add" disabled={busy} className="inline-flex items-center justify-center gap-2 bg-brand-teal hover:bg-brand-tealDark disabled:opacity-60 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Dodaj
            </button>
          </form>
        </div>
        <div className="divide-y divide-border border-t border-border">
          {data && !whitelist.length && <p data-testid="security-whitelist-empty" className="p-6 text-sm text-muted-foreground">Brak zaufanych adresów.</p>}
          {whitelist.map((w) => (
            <div key={w.ip} data-testid={`security-whitelist-${w.ip}`} className="p-4 sm:px-6 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px]">
                <p className="font-mono font-semibold text-sm">{w.ip}</p>
                <p className="text-xs text-muted-foreground">{w.label ? w.label + " · " : ""}dodano {new Date(w.created_at).toLocaleString("pl-PL")}</p>
              </div>
              <button data-testid={`security-whitelist-remove-${w.ip}`} onClick={() => removeWhitelist(w.ip)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-red border border-brand-red/30 rounded-full px-3 py-1.5 hover:bg-brand-red/10"><Trash2 size={13} /> Usuń</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
